import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import '../styles/features.css';

/* ── Progress nav sections ── */
const NAV_SECTIONS = [
  { id: 'fp-voice',        label: 'AI Voice' },
  { id: 'fp-channels',     label: 'Channels' },
  { id: 'fp-analytics',    label: 'Analytics' },
  { id: 'fp-logs',         label: 'Logs' },
  { id: 'fp-crm',          label: 'CRM' },
  { id: 'fp-integrations', label: 'Integrations' },
  { id: 'fp-tools',        label: 'AI Tools' },
];

/* ── Integrations data ── */
const INTEGRATIONS = [
  { name: 'VAPI AI',           category: 'AI',          active: true },
  { name: 'Google Calendar',   category: 'Calendar',    active: true },
  { name: 'Gmail',             category: 'Email',       active: true },
  { name: 'Google Sheets',     category: 'Data',        active: true },
  { name: 'WhatsApp',          category: 'Messaging',   active: true },
  { name: 'SMS',               category: 'Messaging',   active: true },
  { name: 'Stripe',            category: 'Payments',    active: false },
  { name: 'SendGrid',          category: 'Email',       active: false },
  { name: 'Salesforce',        category: 'CRM',         active: false },
  { name: 'HubSpot',           category: 'CRM',         active: false },
  { name: 'Zoho CRM',          category: 'CRM',         active: false },
  { name: 'Streak CRM',        category: 'CRM',         active: false },
  { name: 'Google Contacts',   category: 'CRM',         active: false },
  { name: 'Slack',             category: 'Messaging',   active: false },
  { name: 'Facebook Ads',      category: 'Ads',         active: false },
  { name: 'Google Ads',        category: 'Ads',         active: false },
  { name: 'Google Analytics',  category: 'Analytics',   active: false },
  { name: 'Cal.com',           category: 'Calendar',    active: false },
  { name: 'Cartesia',          category: 'Voice',       active: false },
  { name: 'Xero',              category: 'Accounting',  active: false },
  { name: 'QuickBooks',        category: 'Accounting',  active: false },
  { name: 'Shopify',           category: 'E-commerce',  active: false },
  { name: 'Zendesk',           category: 'Support',     active: false },
  { name: 'Freshdesk',         category: 'Support',     active: false },
  { name: 'Jira',              category: 'Support',     active: false },
  { name: 'Google Maps',       category: 'Location',    active: false },
];

/* ── Voice Mockup ── */
const TRANSCRIPT_LINES = [
  { speaker: 'ai',     text: 'Thank you for calling, this is Sophie. How can I help you today?' },
  { speaker: 'caller', text: "I'd like to book a consultation for this week." },
  { speaker: 'ai',     text: 'I have Thursday at 2pm or Friday at 10am available. Which works?' },
];

function VoiceMockup() {
  const [shownCount, setShownCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setShownCount(c => (c >= TRANSCRIPT_LINES.length ? 1 : c + 1));
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  const transcriptLines = TRANSCRIPT_LINES.slice(0, shownCount);

  const waveHeights = [6,14,22,18,28,12,24,8,20,16,26,10,22,30,14,24,8,18,26,12,20,28,10,16,22,6,18,12];

  return (
    <div className="fp-mockup fp-voice-mockup">
      <div className="fp-voice-mockup__header">
        <div className="fp-voice-mockup__avatar">S</div>
        <div>
          <div className="fp-voice-mockup__name">Sophie — AI Agent</div>
          <div className="fp-voice-mockup__status">
            <span className="fp-live-dot" />
            Live Call · 0:42
          </div>
        </div>
        <div className="fp-voice-mockup__badges">
          <span className="fp-badge fp-badge--blue">11Labs</span>
          <span className="fp-badge fp-badge--purple">VAPI</span>
          <span className="fp-badge fp-badge--green">GPT-4.1</span>
        </div>
      </div>

      <div className="fp-waveform" aria-hidden="true">
        {waveHeights.map((h, i) => (
          <div
            key={i}
            className="fp-waveform__bar"
            style={{ '--wave-h': `${h}px`, animationDelay: `${(i * 0.07).toFixed(2)}s` }}
          />
        ))}
      </div>

      <div className="fp-transcript">
        {transcriptLines.map((line, i) => (
          <div
            key={i}
            className={`fp-transcript-line fp-transcript-line--${line.speaker === 'ai' ? 'ai' : 'user'}`}
          >
            <span className="fp-transcript-line__label">{line.speaker === 'ai' ? 'Sophie' : 'Caller'}</span>
            <span className="fp-transcript-line__text">{line.text}</span>
          </div>
        ))}
      </div>

      <div className="fp-voice-mockup__footer">
        <span className="fp-badge fp-badge--green">
          <span className="fp-live-dot fp-live-dot--sm" /> Recording
        </span>
        <span className="fp-badge fp-badge--ghost">Full Transcript Saved</span>
      </div>
    </div>
  );
}

/* ── Channels Mockup ── */
function ChannelsMockup() {
  const messages = [
    { from: 'user', text: "I'm interested in AI support for my business" },
    { from: 'ai',   text: "I'd love to help! What industry are you in?" },
    { from: 'user', text: "We're a dental clinic with 3 locations" },
    { from: 'ai',   text: 'Perfect — I can show you how Klaivox works for dental clinics. Can I get your name and email?' },
  ];

  const channels = [
    { name: 'Website Chat', color: '#4F8EF7', count: 14 },
    { name: 'WhatsApp',     color: '#22c55e', count: 9 },
    { name: 'SMS',          color: '#6366f1', count: 6 },
    { name: 'Email',        color: '#f59e0b', count: 3 },
    { name: 'Facebook',     color: '#3b82f6', count: 7 },
    { name: 'Instagram',    color: '#ec4899', count: 5 },
  ];

  return (
    <div className="fp-mockup fp-chat-preview">
      <div className="fp-chat-preview__window">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`fp-chat-msg fp-chat-msg--${m.from}`}
            style={{ animationDelay: `${i * 0.5}s` }}
          >
            {m.text}
          </div>
        ))}
        <div className="fp-chat-typing">
          <span /><span /><span />
        </div>
      </div>
      <div className="fp-channel-pills">
        {channels.map((ch, i) => (
          <div key={i} className="fp-channel-pill" style={{ '--ch-color': ch.color }}>
            <span className="fp-channel-pill__dot" />
            <span className="fp-channel-pill__name">{ch.name}</span>
            <span className="fp-channel-pill__count">{ch.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Analytics Mockup ── */
function AnalyticsMockup() {
  const stats = [
    { label: 'Total Calls',    value: '1,284', delta: '+18%' },
    { label: 'Chat Sessions',  value: '3,492', delta: '+34%' },
    { label: 'Leads Captured', value: '847',   delta: '+22%' },
    { label: 'Avg Duration',   value: '2m 18s', delta: '+6%' },
  ];

  const barHeights = [38, 52, 45, 68, 55, 72, 60, 80, 65, 88, 75, 92];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="fp-analytics-mockup">
      <div className="fp-analytics-stats">
        {stats.map((s, i) => (
          <div key={i} className="fp-analytics-stat">
            <div className="fp-analytics-stat__value">{s.value}</div>
            <div className="fp-analytics-stat__label">{s.label}</div>
            <div className="fp-analytics-stat__delta">{s.delta}</div>
          </div>
        ))}
      </div>
      <div className="fp-analytics-chart">
        {barHeights.map((h, i) => (
          <div key={i} className="fp-analytics-bar-wrap">
            <div
              className="fp-analytics-bar"
              style={{ '--bar-h': `${h}%`, animationDelay: `${i * 0.06}s` }}
            />
            <span className="fp-analytics-bar-label">{months[i]}</span>
          </div>
        ))}
      </div>
      <div className="fp-analytics-pills">
        <span className="fp-badge fp-badge--blue">Call Reasons</span>
        <span className="fp-badge fp-badge--purple">Avg Duration by Agent</span>
        <span className="fp-badge fp-badge--ghost">Success Rate</span>
      </div>
    </div>
  );
}

/* ── Logs Mockup ── */
function LogsMockup() {
  const rows = [
    { type: 'Phone', agent: 'Sophie', customer: '+1 416 555 0123', duration: '3:12', cost: '$0.048', status: 'completed' },
    { type: 'Web',   agent: 'Sophie', customer: 'visitor_4821',    duration: '1:47', cost: '$0.022', status: 'completed' },
    { type: 'Phone', agent: 'Sophie', customer: '+1 647 555 9988', duration: '0:23', cost: '$0.006', status: 'failed' },
    { type: 'Web',   agent: 'Sophie', customer: 'visitor_3302',    duration: '5:03', cost: '$0.071', status: 'completed' },
    { type: 'Phone', agent: 'Sophie', customer: '+1 905 555 4411', duration: '2:55', cost: '$0.039', status: 'completed' },
  ];

  return (
    <div className="fp-mockup fp-logs-mockup">
      <div className="fp-logs-table">
        <div className="fp-logs-header">
          <span>Type</span><span>Agent</span><span>Customer</span>
          <span>Duration</span><span>Cost</span><span>Status</span>
        </div>
        {rows.map((r, i) => (
          <div
            key={i}
            className="fp-logs-row"
            style={{ animationDelay: `${i * 0.12}s` }}
          >
            <span className="fp-logs-row__type">{r.type}</span>
            <span>{r.agent}</span>
            <span className="fp-logs-row__customer">{r.customer}</span>
            <span>{r.duration}</span>
            <span>{r.cost}</span>
            <span className={`fp-log-status fp-log-status--${r.status}`}>{r.status}</span>
          </div>
        ))}
      </div>
      <div className="fp-logs-footer">
        <span className="fp-badge fp-badge--ghost">Transcript</span>
        <span className="fp-badge fp-badge--ghost">Recording</span>
        <span className="fp-badge fp-badge--blue">Integration log →</span>
      </div>
    </div>
  );
}

/* ── CRM Mockup ── */
function CrmMockup() {
  const columns = [
    { label: 'New Lead',   color: '#4F8EF7', count: 12, cards: ['Sarah M. — Dental Clinic', 'James R. — Law Firm'] },
    { label: 'Proposal',   color: '#6366f1', count: 7,  cards: ['Emma K. — Real Estate', 'Liam T. — E-Commerce'] },
    { label: 'Closed Won', color: '#22c55e', count: 4,  cards: ['Olivia B. — Healthcare', 'Noah W. — Services'] },
  ];

  return (
    <div className="fp-mockup fp-crm-mockup">
      <div className="fp-crm-pipeline">
        {columns.map((col, i) => (
          <div key={i} className="fp-crm-col">
            <div className="fp-crm-col__header" style={{ color: col.color }}>
              {col.label}
              <span className="fp-crm-col__count">{col.count}</span>
            </div>
            {col.cards.map((card, j) => (
              <div key={j} className="fp-crm-card" style={{ animationDelay: `${(i * 2 + j) * 0.1}s` }}>
                <span className="fp-crm-card__name">{card}</span>
                <span className="fp-badge fp-badge--blue" style={{ fontSize: '10px' }}>AI Lead</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="fp-crm-health">
        <div className="fp-crm-health__label">
          <span>CRM Health Score</span>
          <span className="fp-crm-health__score">87/100</span>
        </div>
        <div className="fp-crm-health__track">
          <div className="fp-crm-health__fill" style={{ '--fill-w': '87%' }} />
        </div>
      </div>
    </div>
  );
}

/* ── Tools Mockup ── */
function ToolsMockup() {
  const files = [
    { name: 'Website content', note: 'auto-synced' },
    { name: 'Company FAQ', note: '48 entries' },
    { name: 'Service pricing guide.pdf', note: '' },
    { name: 'Booking instructions.pdf', note: '' },
  ];

  const fields = [
    { key: 'Name',    value: 'Sarah Mitchell' },
    { key: 'Email',   value: 'sarah@example.com' },
    { key: 'Phone',   value: '+1 416 555 0182' },
    { key: 'Intent',  value: 'Booking',  badge: 'green' },
    { key: 'Sentiment', value: 'Positive', badge: 'blue' },
  ];

  return (
    <div className="fp-tools-mockup">
      <div className="fp-mockup fp-tools-kb">
        <div className="fp-tools-card-label">Knowledge Base</div>
        {files.map((f, i) => (
          <div key={i} className="fp-tools-kb__file">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <span className="fp-tools-kb__name">{f.name}</span>
            {f.note && <span className="fp-tools-kb__note">{f.note}</span>}
          </div>
        ))}
      </div>
      <div className="fp-mockup fp-tools-output">
        <div className="fp-tools-card-label">Lead Captured — Structured Output</div>
        {fields.map((f, i) => (
          <div key={i} className="fp-tools-output__field">
            <span className="fp-tools-output__key">{f.key}</span>
            {f.badge ? (
              <span className={`fp-badge fp-badge--${f.badge}`}>{f.value}</span>
            ) : (
              <span className="fp-tools-output__value">{f.value}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Progress Nav ── */
function ProgressNav({ activeSection }) {
  return (
    <nav className="fp-progress-nav" aria-label="Section navigation">
      {NAV_SECTIONS.map((s, i) => (
        <div key={s.id} className="fp-progress-dot-wrap">
          {i > 0 && <div className="fp-progress-line" />}
          <a
            href={`#${s.id}`}
            className={`fp-progress-dot ${activeSection === s.id ? 'fp-progress-dot--active' : ''}`}
            aria-label={s.label}
            title={s.label}
          >
            <span className="fp-progress-dot__tooltip">{s.label}</span>
          </a>
        </div>
      ))}
    </nav>
  );
}

/* ── Main Component ── */
export default function Features() {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const observers = [];
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <main className="fp-main">
      <ProgressNav activeSection={activeSection} />

      {/* ── Hero ── */}
      <section className="fp-hero" aria-label="Features hero">
        <div className="fp-hero__blobs" aria-hidden="true">
          <div className="fp-hero__blob fp-hero__blob--1" />
          <div className="fp-hero__blob fp-hero__blob--2" />
        </div>
        <div className="fp-section__inner fp-hero__inner">
          <div className="fp-section__text">
            <span className="section-label">Platform Overview</span>
            <h1>Your AI. Your Brand.<br />Every Conversation.</h1>
            <p className="fp-hero__sub">
              Klaivox gives your business a fully branded AI assistant that handles voice calls,
              chat, SMS, WhatsApp, and email — 24/7, under your name.
            </p>
            <div className="fp-hero__stats">
              <div className="fp-stat-pill">26 Integrations</div>
              <div className="fp-stat-pill">5 Channels</div>
              <div className="fp-stat-pill">160+ Languages</div>
            </div>
          </div>
          <div className="fp-hero__mockup">
            <div className="fp-mockup fp-hero-card">
              <div className="fp-voice-mockup__header">
                <div className="fp-voice-mockup__avatar">S</div>
                <div>
                  <div className="fp-voice-mockup__name">Sophie — AI Agent</div>
                  <div className="fp-voice-mockup__status">
                    <span className="fp-live-dot" /> Active across 5 channels
                  </div>
                </div>
                <span className="fp-badge fp-badge--green">Live</span>
              </div>
              <div className="fp-waveform" aria-hidden="true">
                {[6,14,22,18,28,12,24,8,20,16,26,10,22,30,14,24,8,18,26,12,20,28,10,16,22,6,18,12].map((h, i) => (
                  <div key={i} className="fp-waveform__bar" style={{ '--wave-h': `${h}px`, animationDelay: `${(i * 0.07).toFixed(2)}s` }} />
                ))}
              </div>
              <div className="fp-hero-card__stats">
                <div className="fp-hero-card__stat"><span>1,284</span><small>Calls handled</small></div>
                <div className="fp-hero-card__stat"><span>3,492</span><small>Chats</small></div>
                <div className="fp-hero-card__stat"><span>847</span><small>Leads</small></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Voice ── */}
      <section id="fp-voice" className="fp-section" aria-label="AI Voice Assistants">
        <div className="fp-section__inner">
          <AnimatedSection>
            <div className="fp-section__text">
              <span className="fp-section__label">AI Voice</span>
              <h2>Human-Like AI Phone Calls — In Your Brand's Name</h2>
              <p>
                Powered by 11Labs (Eleven Turbo v2), VAPI AI infrastructure, OpenAI GPT-4.1, and
                Deepgram transcription — your AI agent speaks, listens, and responds like a real
                person representing your brand.
              </p>
              <ul className="fp-feature-list">
                {['Inbound & outbound calls', 'Custom agent name + first message', 'Every call recorded + transcribed', 'Web-based and phone number calls', 'Natural human-like voice'].map((item, i) => (
                  <li key={i}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.15}>
            <VoiceMockup />
          </AnimatedSection>
        </div>
      </section>

      {/* ── Multi-Channel Chat ── */}
      <section id="fp-channels" className="fp-section fp-section--reverse" aria-label="Multi-Channel Chat">
        <div className="fp-section__inner">
          <AnimatedSection delay={0.15}>
            <ChannelsMockup />
          </AnimatedSection>
          <AnimatedSection>
            <div className="fp-section__text">
              <span className="fp-section__label">Multi-Channel</span>
              <h2>One AI Agent. Every Channel Your Customers Use.</h2>
              <p>
                Deploy your AI across every channel where customers reach out — all managed from
                one unified inbox. Every conversation saved with full history and cost tracking.
              </p>
              <ul className="fp-feature-list">
                {['Website Chat', 'WhatsApp Business API', 'SMS', 'Email (Gmail + SendGrid)', 'Facebook Messenger', 'Instagram DMs'].map((item, i) => (
                  <li key={i}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Analytics ── */}
      <section id="fp-analytics" className="fp-section fp-section--full" aria-label="Analytics Dashboard">
        <div className="fp-section__full-inner">
          <AnimatedSection>
            <div className="fp-section__full-header">
              <span className="fp-section__label">Analytics</span>
              <h2>Real-Time Analytics. Every Conversation Tracked.</h2>
              <p>See exactly what your AI is doing — calls handled, appointments booked, leads captured.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.15}>
            <AnalyticsMockup />
          </AnimatedSection>
        </div>
      </section>

      {/* ── Logs ── */}
      <section id="fp-logs" className="fp-section" aria-label="Call and Chat Logs">
        <div className="fp-section__inner">
          <AnimatedSection>
            <div className="fp-section__text">
              <span className="fp-section__label">Logs</span>
              <h2>Every Call. Every Chat. Every Detail.</h2>
              <p>
                Full log of every interaction — type, agent, customer, duration, cost, and status.
                Expand any row for the full transcript, recording playback, and integration calls made.
              </p>
              <ul className="fp-feature-list">
                {['Filter by channel, agent, or status', 'Sync and refresh in real time', 'Full transcript per conversation', 'Recording playback built in', 'Integration call history per log'].map((item, i) => (
                  <li key={i}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.15}>
            <LogsMockup />
          </AnimatedSection>
        </div>
      </section>

      {/* ── CRM ── */}
      <section id="fp-crm" className="fp-section fp-section--reverse" aria-label="Built-in CRM">
        <div className="fp-section__inner">
          <AnimatedSection delay={0.15}>
            <CrmMockup />
          </AnimatedSection>
          <AnimatedSection>
            <div className="fp-section__text">
              <span className="fp-section__label">CRM</span>
              <h2>A Full CRM — Built Right In. No Third-Party Needed.</h2>
              <p>
                Contacts, Companies, Deals pipeline, Tasks, Activity feed, and Inbox — all in one
                platform. Your CRM Health Score (0–100) tracks data quality automatically.
              </p>
              <ul className="fp-feature-list">
                {['Custom pipelines, stages & lifecycle stages', 'Automated process rules (e.g. "Contact must have owner")', 'Tags, custom fields, and company linking', 'Activity feed per contact', 'Built-in inbox for all conversations'].map((item, i) => (
                  <li key={i}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Integrations ── */}
      <section id="fp-integrations" className="fp-section fp-section--full" aria-label="26 Integrations">
        <div className="fp-section__full-inner">
          <AnimatedSection>
            <div className="fp-section__full-header">
              <span className="fp-section__label">Integrations</span>
              <h2>26 Native Integrations. More via API.</h2>
              <p>Connect to the tools your business already uses — zero custom development required.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="fp-integrations-grid">
              {INTEGRATIONS.map((int, i) => (
                <div
                  key={i}
                  className={`fp-int-badge animate-child ${int.active ? 'fp-int-badge--active' : ''}`}
                >
                  <span className={`fp-int-dot ${int.active ? 'fp-int-dot--active' : ''}`} />
                  <span className="fp-int-badge__name">{int.name}</span>
                  <span className="fp-int-badge__cat">{int.category}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── AI Tools ── */}
      <section id="fp-tools" className="fp-section" aria-label="AI Tools and Structured Outputs">
        <div className="fp-section__inner">
          <AnimatedSection>
            <div className="fp-section__text">
              <span className="fp-section__label">AI Tools</span>
              <h2>Your AI Learns Your Business. Extracts What Matters.</h2>
              <p>
                Train your agent in minutes using your website, PDFs, and custom instructions.
                Every conversation automatically extracts structured lead data — name, email,
                phone, intent, and sentiment.
              </p>
              <ul className="fp-feature-list">
                {['Knowledge Base: auto-scrape website, upload PDFs', 'Custom AI instructions and personality', 'Structured Outputs: auto-extract lead info', 'AI Tools / Function Calling for custom integrations', 'Background denoising & confidence thresholds', 'Keyterms for domain-specific accuracy'].map((item, i) => (
                  <li key={i}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.15}>
            <ToolsMockup />
          </AnimatedSection>
        </div>
      </section>

      {/* ── Pricing anchor ── */}
      <section className="fp-pricing-anchor-section" aria-label="Pricing">
        <AnimatedSection>
          <div className="fp-pricing-anchor">
            <div className="fp-pricing-anchor__left">
              <span className="fp-pricing-anchor__from">Starts at</span>
              <span className="fp-pricing-anchor__price">$29 <span>CAD/month</span></span>
              <span className="fp-pricing-anchor__usd">~$21 USD · No setup fee · Cancel anytime</span>
            </div>
            <Link to="/pricing" className="btn-secondary fp-pricing-anchor__cta">
              See All Plans →
            </Link>
          </div>
        </AnimatedSection>
      </section>

      {/* ── CTA ── */}
      <section className="cta-banner" aria-label="Call to action">
        <div className="cta-banner__blob" aria-hidden="true" />
        <AnimatedSection>
          <div className="cta-banner__inner">
            <h2>See Every Feature Running Live — in 30 Minutes</h2>
            <p className="cta-banner__sub">
              No long-term contracts <span>·</span> Your brand, not ours <span>·</span> Live in under 24 hours
            </p>
            <Link to="/contact" className="btn-primary" aria-label="Book a free demo">
              Book My Free 30-Min Demo →
            </Link>
          </div>
        </AnimatedSection>
      </section>
    </main>
  );
}
